/**
 * Hook centralisé pour l'archivage et le désarchivage des chantiers
 *
 * Ce hook gère :
 * - La sauvegarde du statut avant archivage
 * - La restauration du statut lors du désarchivage
 * - La validation des règles d'archivage
 * - Les messages utilisateur uniformes
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { canBeArchived, STATUTS_CONFIG } from '@/constants/statuts';

interface UseChantierArchiveProps {
  onSuccess?: () => void;
}

interface ChantierMinimal {
  id: string;
  etat_chantier: string;
  etat_avant_archivage?: string | null;
}

export function useChantierArchive({ onSuccess }: UseChantierArchiveProps = {}) {
  const [loading, setLoading] = useState(false);

  /**
   * Archiver un chantier
   * - Vérifie que le chantier peut être archivé (brouillon, termine, annule)
   * - Sauvegarde l'état actuel dans etat_avant_archivage
   * - Passe le statut à 'archive'
   */
  const archiver = useCallback(async (chantier: ChantierMinimal): Promise<boolean> => {
    // Vérifier si le chantier peut être archivé
    if (!canBeArchived(chantier.etat_chantier)) {
      const statutLabel = STATUTS_CONFIG[chantier.etat_chantier]?.label || chantier.etat_chantier;
      toast.error(`Un chantier "${statutLabel}" ne peut pas être archivé. Terminez-le ou annulez-le d'abord.`);
      return false;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('chantiers')
        .update({
          etat_avant_archivage: chantier.etat_chantier,
          etat_chantier: 'archive',
          statut: 'archive' // Mettre à jour aussi le champ statut pour la compatibilité
        })
        .eq('id', chantier.id);

      if (error) throw error;

      toast.success('Chantier archivé avec succès.');
      onSuccess?.();
      return true;
    } catch (error: any) {
      console.error('Erreur archivage:', error);
      toast.error(error.message || "Erreur lors de l'archivage.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);

  /**
   * Désarchiver un chantier
   * - Restaure l'état depuis etat_avant_archivage
   * - Utilise 'termine' par défaut si pas d'état sauvegardé
   * - Nettoie le champ de sauvegarde
   */
  const desarchiver = useCallback(async (chantier: ChantierMinimal): Promise<boolean> => {
    setLoading(true);
    try {
      // Utiliser l'état sauvegardé, ou 'termine' par défaut pour les anciens chantiers
      const etatRestaure = chantier.etat_avant_archivage || 'termine';

      const { error } = await supabase
        .from('chantiers')
        .update({
          etat_chantier: etatRestaure,
          etat_avant_archivage: null,
          statut: 'actif' // Remettre le statut à 'actif' pour la compatibilité
        })
        .eq('id', chantier.id);

      if (error) throw error;

      const statutLabel = STATUTS_CONFIG[etatRestaure]?.label || etatRestaure;
      toast.success(`Chantier désarchivé. Statut restauré : "${statutLabel}".`);
      onSuccess?.();
      return true;
    } catch (error: any) {
      console.error('Erreur désarchivage:', error);
      toast.error(error.message || 'Erreur lors du désarchivage.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);

  return {
    loading,
    archiver,
    desarchiver
  };
}
